import React, { useState, useEffect } from 'react'
import { CalendarIcon, ClockIcon, UserIcon, DocumentTextIcon } from '@heroicons/react/24/outline'
import { getLocalApiUrl, authFetch } from '../../config/api'

const ACTIVITY_TYPES = {
  LOGIN: { label: 'Login', color: 'bg-green-100 text-green-800' },
  LOGOUT: { label: 'Logout', color: 'bg-gray-100 text-gray-800' },
  RULE: { label: 'Rule', color: 'bg-blue-100 text-blue-800' },
  RULE_CREATED: { label: 'Rule Created', color: 'bg-blue-100 text-blue-800' },
  RULE_MODIFIED: { label: 'Rule Modified', color: 'bg-yellow-100 text-yellow-800' },
  RULE_DELETED: { label: 'Rule Deleted', color: 'bg-red-100 text-red-800' },
  CASE: { label: 'Case', color: 'bg-orange-100 text-orange-800' },
  CASE_CREATED: { label: 'Case Created', color: 'bg-orange-100 text-orange-800' },
  CASE_UPDATED: { label: 'Case Updated', color: 'bg-indigo-100 text-indigo-800' },
  ALERT: { label: 'Alert', color: 'bg-amber-100 text-amber-800' },
  DVS: { label: 'DVS', color: 'bg-red-100 text-red-800' },
  MEMBER: { label: 'Member', color: 'bg-teal-100 text-teal-800' },
  REPORT: { label: 'Report', color: 'bg-violet-100 text-violet-800' },
}
const DEFAULT_TYPE = { label: 'Activity', color: 'bg-gray-100 text-gray-800' }

function ActivityLogs() {
  const [activities, setActivities] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('ALL')
  const [dateRange, setDateRange] = useState('today')

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        const params = new URLSearchParams()
        if (filter !== 'ALL') params.set('type', filter)
        const qs = params.toString()
        const url = getLocalApiUrl(`/api/activity-logs${qs ? '?' + qs : ''}`)
        const res = await authFetch(url)
        const json = await res.json()
        if (json.success) {
          setActivities(json.data)
        }
      } catch (err) {
        console.error('Failed to fetch activity logs:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchActivities()
  }, [filter])

  const filteredActivities = activities.filter(activity => 
    filter === 'ALL' || activity.type === filter
  )

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString()
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Activity Logs</h1>
        <p className="text-gray-600">
          Track all user activities and system events in real-time.
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Activity Type
            </label>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="select-field"
            >
              <option value="ALL">All Activities</option>
              {Object.entries(ACTIVITY_TYPES).map(([key, type]) => (
                <option key={key} value={key}>{type.label}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date Range
            </label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="select-field"
            >
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="quarter">This Quarter</option>
            </select>
          </div>

          <div className="flex items-end">
            <button className="btn-secondary w-full">
              Export Logs
            </button>
          </div>
        </div>
      </div>

      {/* Activity List */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">
            Recent Activities ({filteredActivities.length})
          </h2>
        </div>
        
        <div className="divide-y divide-gray-200">
          {filteredActivities.map((activity) => (
            <div key={activity.id} className="p-6 hover:bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                      <DocumentTextIcon className="w-5 h-5 text-gray-500" />
                    </div>
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${(ACTIVITY_TYPES[activity.type] || DEFAULT_TYPE).color}`}>
                        {(ACTIVITY_TYPES[activity.type] || DEFAULT_TYPE).label}
                      </span>
                      <div className="flex items-center text-sm text-gray-500">
                        <UserIcon className="w-4 h-4 mr-1" />
                        {activity.user}
                      </div>
                    </div>
                    
                    <p className="text-sm font-medium text-gray-900 mb-1">
                      {activity.description}
                    </p>
                    
                    <div className="flex items-center text-sm text-gray-500">
                      <ClockIcon className="w-4 h-4 mr-1" />
                      {formatTimestamp(activity.timestamp)}
                    </div>
                    
                    {activity.details && (
                      <div className="mt-2 p-3 bg-gray-50 rounded-md">
                        <pre className="text-xs text-gray-600 whitespace-pre-wrap">
                          {JSON.stringify(activity.details, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default ActivityLogs